import React, { createContext, useContext, useState, useEffect } from 'react';

export interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  read: boolean;
  date: Date;
  actionUrl?: string;
  actionLabel?: string;
}

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  addNotification: (notification: Omit<Notification, 'id' | 'date' | 'read'>) => void;
  markAsRead: (notificationId: string) => void;
  markAllAsRead: () => void;
  removeNotification: (notificationId: string) => void;
  clearAll: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

// Mock notifications
const mockNotifications: Notification[] = [
  {
    id: '1',
    title: 'Order Shipped',
    message: 'Your order #ORD-1234 has been shipped and is on its way!',
    type: 'success',
    read: false,
    date: new Date(Date.now() - 1000 * 60 * 30), // 30 minutes ago
    actionUrl: '/orders/ORD-1234',
    actionLabel: 'Track Order'
  },
  {
    id: '2',
    title: 'New Product Available',
    message: 'Check out the latest Catapult devices with enhanced GPS accuracy!',
    type: 'info',
    read: false,
    date: new Date(Date.now() - 1000 * 60 * 60 * 2), // 2 hours ago
    actionUrl: '/devices',
    actionLabel: 'View Product'
  },
  {
    id: '3',
    title: 'Payment Reminder',
    message: 'Your subscription renewal is due in 3 days.',
    type: 'warning',
    read: true,
    date: new Date(Date.now() - 1000 * 60 * 60 * 24), // 1 day ago
    actionUrl: '/account',
    actionLabel: 'Update Payment'
  }
];

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>(mockNotifications);

  useEffect(() => {
    // Load notifications from localStorage
    const storedNotifications = localStorage.getItem('user_notifications');
    if (storedNotifications) {
      const parsedNotifications = JSON.parse(storedNotifications).map((notification: any) => ({
        ...notification,
        date: new Date(notification.date),
      }));
      setNotifications([...mockNotifications, ...parsedNotifications]);
    }
  }, []);

  const saveNotifications = (updatedNotifications: Notification[]) => {
    setNotifications(updatedNotifications);
    const userNotifications = updatedNotifications.filter(n => !mockNotifications.find(mn => mn.id === n.id));
    localStorage.setItem('user_notifications', JSON.stringify(userNotifications));
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  const addNotification = (notificationData: Omit<Notification, 'id' | 'date' | 'read'>) => {
    const newNotification: Notification = {
      ...notificationData,
      id: `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      date: new Date(),
      read: false,
    };

    const updatedNotifications = [newNotification, ...notifications];
    saveNotifications(updatedNotifications);
  };

  const markAsRead = (notificationId: string) => {
    const updatedNotifications = notifications.map(notification =>
      notification.id === notificationId
        ? { ...notification, read: true }
        : notification
    );
    saveNotifications(updatedNotifications);
  };

  const markAllAsRead = () => {
    const updatedNotifications = notifications.map(notification => ({
      ...notification,
      read: true
    }));
    saveNotifications(updatedNotifications);
  };

  const removeNotification = (notificationId: string) => {
    const updatedNotifications = notifications.filter(n => n.id !== notificationId);
    saveNotifications(updatedNotifications);
  };

  const clearAll = () => {
    saveNotifications([]);
  };

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        addNotification,
        markAsRead,
        markAllAsRead,
        removeNotification,
        clearAll,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};